import { useQuery } from '@tanstack/react-query';
import { Activity, Building2, Users, AlertTriangle } from 'lucide-react';
import { api } from '../api/client';
import { StatCard, LoadingState, ErrorState, PageHeader, Table, Th, Td, Badge } from '../components/ui';

interface DashboardData {
  entityCount: number;
  suspendedCount: number;
  planCounts: Record<string, number>;
  activeUsersLast5Min: number;
}

export function OpsDashboard() {
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['admin', 'ops', 'dashboard'],
    queryFn: () => api.get<DashboardData>('/ops/dashboard'),
    refetchInterval: 30_000,
  });

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Platform health at a glance" />

      {isLoading && <LoadingState />}
      {error && <ErrorState message="Error loading dashboard" />}

      {data && (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <StatCard
              label="Total Entities"
              value={data.entityCount}
              sub="registered orgs"
            />
            <StatCard
              label="Suspended"
              value={data.suspendedCount}
              variant={data.suspendedCount > 0 ? 'warning' : 'default'}
            />
            <StatCard
              label="Active (5 min)"
              value={data.activeUsersLast5Min}
              sub="online users"
              variant={data.activeUsersLast5Min === 0 ? 'default' : 'default'}
            />
            <StatCard
              label="Plan Mix"
              value={Object.keys(data.planCounts).length}
              sub="distinct plans"
            />
          </div>

          {/* Plan breakdown */}
          {Object.keys(data.planCounts).length > 0 && (
            <div className="mb-6">
              <h2 className="text-sm font-medium text-gray-400 mb-2">Plan Breakdown</h2>
              <Table>
                <thead>
                  <tr>
                    <Th>Plan</Th>
                    <Th>Entities</Th>
                    <Th>Share</Th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(data.planCounts).map(([plan, count]) => (
                    <tr key={plan} className="hover:bg-white/[0.02] transition-colors">
                      <Td>
                        <Badge variant={plan === 'free' ? 'default' : 'info'}>
                          {plan}
                        </Badge>
                      </Td>
                      <Td className="font-medium">{count}</Td>
                      <Td className="text-gray-400">
                        {data.entityCount > 0
                          ? `${Math.round((count / data.entityCount) * 100)}%`
                          : '—'}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {/* Status indicators */}
          <div className="flex flex-wrap gap-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-surface border border-white/[0.06] text-xs text-gray-400">
              <Activity className="h-3.5 w-3.5 text-emerald-400" />
              <span>Auto-refresh every 30s</span>
            </div>
            {data.suspendedCount > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-400">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>{data.suspendedCount} suspended {data.suspendedCount === 1 ? 'entity' : 'entities'}</span>
              </div>
            )}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-surface border border-white/[0.06] text-xs text-gray-400">
              <Building2 className="h-3.5 w-3.5 text-primary" />
              <span>{data.entityCount} total entities</span>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-bg-surface border border-white/[0.06] text-xs text-gray-400">
              <Users className="h-3.5 w-3.5 text-primary" />
              <span>{data.activeUsersLast5Min} active now</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
